import type { Notification } from './api'

const ACTOR_PATTERNS: Partial<Record<Notification['type'], RegExp>> = {
  app_shared: /^(.*?) shared /,
  access_request: /^(.*?) requested access /,
  production_build_triggered: /^(.*?) started a production build /,
}

function extractActorTerm(type: Notification['type'], message: string): string | null {
  const match = ACTOR_PATTERNS[type]?.exec(message)
  return match?.[1] ?? null
}

function preferredActor(notif: Notification): string | null {
  const data = notif.data || {}
  const candidates = [
    data.shared_by,
    data.requester_email,
    data.triggered_by,
    data.name,
    data.email,
  ]

  return candidates.find((value): value is string => typeof value === 'string' && value.trim().length > 0) ?? null
}

function actorDisplayName(actor: string): string {
  return actor.includes('@') ? actor.split('@')[0] : actor
}

function normalizeNotificationMessage(notif: Notification): string {
  const currentActor = extractActorTerm(notif.type, notif.message)
  const fallbackActor = preferredActor(notif)

  if (!currentActor || !fallbackActor) {
    return notif.message
  }

  if (currentActor.trim().toLowerCase() !== 'new') {
    return notif.message
  }

  return notif.message.replace(currentActor, actorDisplayName(fallbackActor))
}

/** Render notification message with bold actor names and app names */
export function formatNotificationMessage(notif: Notification): React.ReactNode {
  const data = notif.data || {}
  const message = normalizeNotificationMessage(notif)
  const boldTerms = new Set<string>()

  const quotedTerms = Array.from(message.matchAll(/'([^']+)'/g), (match) => `'${match[1]}'`)
  quotedTerms.forEach((term) => boldTerms.add(term))

  const actorCandidates = [
    extractActorTerm(notif.type, message),
    preferredActor(notif),
    data.name,
    data.email,
    data.shared_by,
    data.requester_email,
    data.triggered_by,
  ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0)

  actorCandidates.forEach((candidate) => {
    if (message.includes(candidate)) {
      boldTerms.add(candidate)
    }
    if (candidate.includes('@')) {
      const localPart = candidate.split('@')[0]
      if (localPart && message.includes(localPart)) {
        boldTerms.add(localPart)
      }
    }
  })

  // Secret scan count pattern
  if (notif.type === 'secret_scan_finding' && data.total) {
    boldTerms.add(`${data.total} secret(s)`)
  }

  // Feedback — bold the submitted content after "submitted: "
  if (notif.type === 'new_feedback') {
    const match = message.match(/submitted: (.+)$/)
    if (match) boldTerms.add(match[1])
  }

  if (boldTerms.size === 0) return message

  // Build a regex that matches any of the bold terms
  const escaped = Array.from(boldTerms).map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const re = new RegExp(`(${escaped.join('|')})`, 'g')
  const parts = message.split(re)

  return parts.map((part, i) =>
    boldTerms.has(part)
      ? <strong key={i} className="font-semibold text-foreground">{part}</strong>
      : part
  )
}
